import { trackHowlListening } from '@/utils/listeningActivity'
import { ref, watch } from 'vue'
import { Howl } from 'howler'
import { formatTimeCompact } from '@/utils/format'

/**
 * 音频播放 composable
 * 管理 Howl 实例、播放状态、时间更新、进度条、音量、倍速
 *
 * @param {Object} options
 * @param {import('vue').Ref<string>} options.audioSrc - 音频 URL
 * @param {import('vue').ComputedRef<Array>} options.displaySubtitles - 当前字幕数组
 * @param {import('vue').Ref<string>} options.playMode - 播放模式 ('auto' | 'reading')
 * @param {Function} options.onPauseCallback - 暂停时回调（给 readAlong.onPause）
 * @param {Function} options.onEndedCallback - 播放结束回调
 * @param {Function} options.onErrorCallback - 音频错误回调
 */
export function useAudioPlayback({
  audioSrc,
  displaySubtitles,
  playMode,
  onErrorCallback
}) {
  // --- State ---
  const sound = ref(null)

  // 延迟注入的回调（组件 methods 中定义后再注入）
  let _onPauseCallback = null
  let _onEndedCallback = null
  let _onPlayCallback = null
  function setOnPauseCallback(fn) { _onPauseCallback = fn }
  function setOnEndedCallback(fn) { _onEndedCallback = fn }
  function setOnPlayCallback(fn) { _onPlayCallback = fn }
  const isPlaying = ref(false)
  const activeSoundId = ref(null)
  const currentTime = ref(0)
  const audioDuration = ref(0)
  const volume = ref(1)
  const playbackRate = ref(1)
  const progressPercentage = ref(0)
  const currentSubtitleIndex = ref(0)
  const audioError = ref(null)
  const tooltipPosition = ref(0)
  const tooltipTime = ref('00:00')
  const showTooltip = ref(false)

  let timeUpdateFrame = null
  let suppressedPauseEvents = 0

  function getSubtitleList() {
    return Array.isArray(displaySubtitles.value) ? displaySubtitles.value : []
  }

  function findSubtitleIndexAtTime(time, hintIndex = currentSubtitleIndex.value) {
    const subtitles = getSubtitleList()
    if (!subtitles.length) return -1

    const hinted = subtitles[hintIndex]
    if (hinted && time >= hinted.start && time <= hinted.end) {
      return hintIndex
    }

    const next = subtitles[hintIndex + 1]
    if (next && time >= next.start && time <= next.end) {
      return hintIndex + 1
    }

    const prev = subtitles[hintIndex - 1]
    if (prev && time >= prev.start && time <= prev.end) {
      return hintIndex - 1
    }

    let left = 0
    let right = subtitles.length - 1

    while (left <= right) {
      const mid = Math.floor((left + right) / 2)
      const subtitle = subtitles[mid]

      if (time < subtitle.start) {
        right = mid - 1
      } else if (time > subtitle.end) {
        left = mid + 1
      } else {
        return mid
      }
    }

    return -1
  }

  // ==================== 音频初始化 ====================

  function initAudio() {
    if (!audioSrc.value) return

    if (sound.value) {
      sound.value.stop()
      sound.value.unload()
      sound.value = null
    }

    isPlaying.value = false
    currentTime.value = 0
    progressPercentage.value = 0
    currentSubtitleIndex.value = 0

    sound.value = new Howl({
      src: [audioSrc.value],
      html5: true,
      preload: true,
      onload: () => {
        audioDuration.value = sound.value.duration()
      },
      onend: (soundId) => {
        if (soundId === activeSoundId.value) {
          activeSoundId.value = null
        }
        _onEndedCallback?.()
      },
      onseek: (soundId) => {
        if (soundId !== undefined && soundId !== null) {
          activeSoundId.value = soundId
        }
        startTimeUpdate()
      },
      onstop: (soundId) => {
        if (soundId === activeSoundId.value) {
          activeSoundId.value = null
        }
        isPlaying.value = false
      },
      onpause: (soundId) => {
        if (soundId !== undefined && soundId !== null) {
          activeSoundId.value = soundId
        }
        const shouldSuppressCallback = suppressedPauseEvents > 0
        if (shouldSuppressCallback) suppressedPauseEvents -= 1
        if (!shouldSuppressCallback) _onPauseCallback?.()
      },
      onplay: (soundId) => {
        activeSoundId.value = soundId
        isPlaying.value = true
        startTimeUpdate()
        _onPlayCallback?.()
      },
      onloaderror: handleAudioError,
      onplayerror: handleAudioError
    })

    trackHowlListening(sound.value)

    if (sound.value) {
      sound.value.volume(volume.value)
    }
  }

  // ==================== 时间更新 ====================

  function startTimeUpdate() {
    const updateTime = () => {
      if (sound.value && isPlaying.value) {
        currentTime.value = sound.value.seek()
        progressPercentage.value = (currentTime.value / audioDuration.value) * 100
        checkCurrentSubtitle()
        timeUpdateFrame = requestAnimationFrame(updateTime)
      }
    }
    updateTime()
  }

  function stopTimeUpdate() {
    if (timeUpdateFrame) {
      cancelAnimationFrame(timeUpdateFrame)
      timeUpdateFrame = null
    }
  }

  function getCurrentSoundId() {
    if (activeSoundId.value !== null && activeSoundId.value !== undefined) {
      return activeSoundId.value
    }

    const sounds = sound.value?._sounds
    if (!Array.isArray(sounds) || !sounds.length) return null

    const candidate = sounds.find((item) => !item._ended) || sounds[sounds.length - 1]
    return candidate?._id ?? null
  }

  function playCurrent() {
    if (!sound.value) return null

    const soundId = getCurrentSoundId()
    if (soundId !== null) {
      activeSoundId.value = soundId
      sound.value.play(soundId)
      return soundId
    }

    const newSoundId = sound.value.play()
    activeSoundId.value = newSoundId
    return newSoundId
  }

  function pauseCurrent({ suppressCallback = false } = {}) {
    if (!sound.value) return

    const soundId = getCurrentSoundId()
    if (soundId !== null) {
      if (!sound.value.playing(soundId)) return
      if (suppressCallback) suppressedPauseEvents += 1
      sound.value.pause(soundId)
      return
    }

    if (!sound.value.playing()) return
    if (suppressCallback) suppressedPauseEvents += 1
    sound.value.pause()
  }

  function stopCurrent() {
    if (!sound.value) return

    const soundId = getCurrentSoundId()
    if (soundId !== null) {
      sound.value.stop(soundId)
      if (activeSoundId.value === soundId) {
        activeSoundId.value = null
      }
      return
    }

    sound.value.stop()
    activeSoundId.value = null
  }

  function seekCurrent(time) {
    if (!sound.value) return

    const soundId = getCurrentSoundId()
    if (soundId !== null) {
      sound.value.seek(time, soundId)
      return
    }

    sound.value.seek(time)
  }

  function isCurrentPlaying() {
    if (!sound.value) return false

    const soundId = getCurrentSoundId()
    if (soundId !== null) {
      return sound.value.playing(soundId)
    }

    return sound.value.playing()
  }

  // ==================== 字幕跟踪 ====================

  function checkCurrentSubtitle() {
    const subtitles = getSubtitleList()
    if (!subtitles.length) return

    if (playMode.value === 'reading' && isPlaying.value) {
      const subtitle = subtitles[currentSubtitleIndex.value]
      if (subtitle && currentTime.value >= subtitle.end) {
        pauseCurrent({ suppressCallback: true })
        isPlaying.value = false
        seekCurrent(Math.max(Number(subtitle.start) || 0, Number(subtitle.end) - 0.01))
      }
    } else if (playMode.value === 'auto') {
      const subtitleIndex = findSubtitleIndexAtTime(currentTime.value)
      if (subtitleIndex !== -1 && subtitleIndex !== currentSubtitleIndex.value) {
        currentSubtitleIndex.value = subtitleIndex
      }
    }
  }

  function getCurrentSubtitle() {
    const subtitles = getSubtitleList()
    const subtitleIndex = findSubtitleIndexAtTime(currentTime.value)
    return subtitleIndex === -1 ? null : subtitles[subtitleIndex]
  }

  // ==================== 进度条 ====================

  function handleProgressClick(event) {
    const progressBar = event.currentTarget
    const subtitles = getSubtitleList()
    const rect = progressBar.getBoundingClientRect()
    const x = event.clientX - rect.left
    const percentage = (x / rect.width) * 100
    const time = (percentage / 100) * audioDuration.value

    if (sound.value) {
      seekCurrent(time)
      currentTime.value = time
      progressPercentage.value = percentage

      const subtitleIndex = findSubtitleIndexAtTime(time)
      if (subtitleIndex !== -1) {
        currentSubtitleIndex.value = subtitleIndex
      }

      if (playMode.value === 'reading' && isPlaying.value) {
        const subtitle = subtitles[currentSubtitleIndex.value]
        if (subtitle) {
          seekCurrent(subtitle.start)
          currentTime.value = subtitle.start
          progressPercentage.value = (subtitle.start / audioDuration.value) * 100
        }
      }
    }
  }

  function handleProgressHover(event) {
    const progressBar = event.currentTarget
    if (!progressBar) return
    const rect = progressBar.getBoundingClientRect()
    const offsetX = event.clientX - rect.left
    const percentage = (offsetX / rect.width) * 100
    if (percentage >= 0 && percentage <= 100) {
      tooltipPosition.value = offsetX
      tooltipTime.value = formatTime((percentage / 100) * audioDuration.value)
      showTooltip.value = true
    }
  }

  function hideTimeTooltip() {
    showTooltip.value = false
  }

  // ==================== 音量 / 倍速 ====================

  function setVolume(value) {
    volume.value = value
    if (sound.value) sound.value.volume(value)
  }

  function setPlaybackRate(rate) {
    playbackRate.value = rate
    if (sound.value) sound.value.rate(rate)
  }

  // ==================== 错误处理 ====================

  function handleAudioError(error) {
    audioError.value = error
    onErrorCallback?.(error)
  }

  // ==================== 工具 ====================

  function formatTime(time) {
    return formatTimeCompact(time)
  }

  // ==================== Watcher ====================

  watch(audioSrc, (newSrc) => {
    if (sound.value) {
      sound.value.stop()
      sound.value.unload()
      sound.value = null
    }
    if (newSrc) {
      initAudio()
    }
  }, { immediate: true })

  watch(displaySubtitles, (subtitles) => {
    if (!subtitles?.length) {
      currentSubtitleIndex.value = 0
      return
    }

    const subtitleIndex = findSubtitleIndexAtTime(currentTime.value)
    if (subtitleIndex !== -1) {
      currentSubtitleIndex.value = subtitleIndex
    } else {
      currentSubtitleIndex.value = 0
    }
  }, { immediate: true })

  // ==================== 清理 ====================

  function cleanupPlayback() {
    if (sound.value) {
      sound.value.stop()
      sound.value.unload()
      sound.value = null
      activeSoundId.value = null
      isPlaying.value = false
      suppressedPauseEvents = 0
      currentTime.value = 0
      progressPercentage.value = 0
      currentSubtitleIndex.value = 0
    }
    stopTimeUpdate()
  }

  return {
    // 状态
    sound,
    isPlaying,
    currentTime,
    audioDuration,
    volume,
    playbackRate,
    progressPercentage,
    currentSubtitleIndex,
    activeSoundId,
    audioError,
    tooltipPosition,
    tooltipTime,
    showTooltip,
    // 方法
    initAudio,
    startTimeUpdate,
    stopTimeUpdate,
    checkCurrentSubtitle,
    getCurrentSubtitle,
    handleProgressClick,
    handleProgressHover,
    hideTimeTooltip,
    setVolume,
    setPlaybackRate,
    handleAudioError,
    formatTime,
    playCurrent,
    pauseCurrent,
    stopCurrent,
    seekCurrent,
    isCurrentPlaying,
    cleanupPlayback,
    setOnPauseCallback,
    setOnEndedCallback,
    setOnPlayCallback
  }
}
